import "./App.css";
import {Suspense, lazy, useEffect, useRef} from "react";
import {
	Switch,
	Route,
	Redirect,
	useHistory,
	useRouteMatch,
} from "react-router-dom";
import Toast from "./components/Toast";
import ApiErrorHandler from "./components/ApiErrorHandler";
import WithBackButton from "./components/WithBackButton";
import TopBar from "./components/TopBar";
import EventNameWrapper from "./components/EventNameWrapper";
import {useUser} from "./contexts/AuthContext";
import Navigation from "./components/Navigation";
import GoogleTagManager from "./components/GoogleTagManager";
import ErrorBoundary from "@honeybadger-io/react";
import honeybadger from "./honeybadger";
import {useState, createContext} from "react";
import {ThemeProvider} from "./contexts/ThemeContext";
import {useLanguage} from "./contexts/LanguageContext";

const Login = lazy(() => import("./views/LoginView"));
const MagicLogin = lazy(() => import("./views/MagicLoginView"));
const NewInvite = lazy(() => import("./views/NewInviteView"));
const Register = lazy(() => import("./views/RegisterView"));
const TermsOfService = lazy(() => import("./views/TermsOfService"));

const EventParticipants = lazy(() => import("./views/EventParticipants"));
const Partners = lazy(() => import("./views/Partners"));
const Events = lazy(() => import("./views/Events"));
const Onboarding = lazy(() => import("./views/Onboarding"));
const Home = lazy(() => import("./views/Home"));
const Calendar = lazy(() => import("./views/Calendar"));
const ForgotPassword = lazy(() => import("./views/ForgotPassword"));
const ResetPassword = lazy(() => import("./views/ResetPassword"));
const Partner = lazy(() => import("./views/Partner"));
const Recording = lazy(() => import("./views/Recording"));
const Streams = lazy(() => import("./views/Streams"));
const User = lazy(() => import("./views/User"));
const QrScanView = lazy(() => import("./views/QrScanView"));
const NotFound = lazy(() => import("./views/NotFound"));
const PartnerHubRedirect = lazy(() => import("./views/PartnerHubRedirect"));

export const LocationContext = createContext(null);
const LocationProvider = ({children}) => {
	const [previous, setPrevious] = useState(null);

	return (
		<LocationContext.Provider value={{previous, setPrevious}}>
			{children}
		</LocationContext.Provider>
	);
};

const AuthenticatedRoute = ({children}) => {
};

function App() {
	const user = useUser();
	const refContainer = useRef(null);
	const { translations } = useLanguage();
	const isStreamsRoute = useRouteMatch("/streams");
	const isRecordingsRoute = useRouteMatch("/recordings");

	const ErrorComponent = () => (
		<h1 className="center sans margin-48 margin-top">
			{translations.error}
		</h1>
	);

	/* pre clock side change*/
	return (
		<LocationProvider>
			{/* A <Switch> looks through its children <Route>s and
          renders the first one that matches the current URL. */}
			<GoogleTagManager/>
			<ThemeProvider>
				<Suspense
					fallback={
						<>
							<main ref={refContainer}>
								<TopBar hideWhenAdmin user={user}/>
							</main>

							<Navigation hideWhenAdmin user={user}/>
						</>
					}>
					{user ? (
						<>
							<main className={(isStreamsRoute || isRecordingsRoute) ? "stream-layout" : null} ref={refContainer}>
								<TopBar hideWhenAdmin user={user}/>
								<Switch>
									<Route exact path="/">
										<Redirect to="/events"/>
									</Route>

									<Route exact path="/login" render={({ location }) => {
										const searchParams = new URLSearchParams(location.search || "");
										const returnUrl = searchParams.get("return");
										const to = returnUrl && returnUrl.startsWith("/") && !returnUrl.startsWith("//") ? returnUrl : "/events";
										return <Redirect to={to} />;
									}} />

									<Route exact path="/register">
										<Redirect to="/"/>
									</Route>

									<Route path="/events" exact component={Events}/>

									<Route
										path="/user"
										render={() => (
											<WithBackButton>
												<EventNameWrapper showTitle={false}>
													<ErrorBoundary
														honeybadger={honeybadger}
														ErrorComponent={ErrorComponent}>
														<User user={user}/>
													</ErrorBoundary>
												</EventNameWrapper>
											</WithBackButton>
										)}
									/>
									<Route
										path="/scan/:scanId"
										render={() => (
											<WithBackButton>
												<EventNameWrapper>
													<ErrorBoundary
														honeybadger={honeybadger}
														ErrorComponent={ErrorComponent}>
														<QrScanView/>
													</ErrorBoundary>
												</EventNameWrapper>
											</WithBackButton>
										)}
									/>
									<Route
										path="/home"
										exact
										render={() => (
											<WithBackButton>
												<EventNameWrapper>
													<ErrorBoundary
														honeybadger={honeybadger}
														ErrorComponent={ErrorComponent}>
														<Home user={user}/>
													</ErrorBoundary>
												</EventNameWrapper>
											</WithBackButton>
										)}
									/>

									<Route
										path="/onboarding"
										exact
										component={() => (
											<ErrorBoundary
												honeybadger={honeybadger}
												ErrorComponent={ErrorComponent}>
												<Onboarding user={user}/>
											</ErrorBoundary>
										)}
									/>

									<Route
										path="/participants"
										exact
										component={() => (
											<WithBackButton>
												<EventNameWrapper>
													<ErrorBoundary
														honeybadger={honeybadger}
														ErrorComponent={ErrorComponent}>
														<EventParticipants user={user}/>
													</ErrorBoundary>
												</EventNameWrapper>
											</WithBackButton>
										)}
									/>

									<Route
										path="/calendar"
										exact
										component={() => (
											<WithBackButton>
												<EventNameWrapper>
													<ErrorBoundary
														honeybadger={honeybadger}
														ErrorComponent={ErrorComponent}>
														<Calendar user={user}/>
													</ErrorBoundary>
												</EventNameWrapper>
											</WithBackButton>
										)}
									/>

									<Route
										path="/partners"
										exact
										component={() => (
											<WithBackButton>
												<EventNameWrapper>
													<ErrorBoundary
														honeybadger={honeybadger}
														ErrorComponent={ErrorComponent}>
														<Partners user={user}/>
													</ErrorBoundary>
												</EventNameWrapper>
											</WithBackButton>
										)}
									/>
									<Route
										path="/partners/partner"
										exact
										component={() => (
											<WithBackButton to="/partners">
												<EventNameWrapper>
													<ErrorBoundary>
														<Partner user={user}/>
													</ErrorBoundary>
												</EventNameWrapper>
											</WithBackButton>
										)}
									/>

									<Route
										path="/recordings/recording"
										exact
										component={() => (
											<WithBackButton to="/calendar">
												<EventNameWrapper>
													<ErrorBoundary>
														<Recording user={user}/>
													</ErrorBoundary>
												</EventNameWrapper>
											</WithBackButton>
										)}
									/>

									<Route
										path="/partner"
										exact
										component={() => (
											<WithBackButton>
												<EventNameWrapper>
													<ErrorBoundary honeybadger={honeybadger}>
														<Partner user={user}/>
													</ErrorBoundary>
												</EventNameWrapper>
											</WithBackButton>
										)}
									/>

									<Route
										path="/streams"
										exact
										component={() => (
											<WithBackButton>
												<EventNameWrapper>
													<ErrorBoundary honeybadger={honeybadger}>
														<Streams user={user}/>
													</ErrorBoundary>
												</EventNameWrapper>
											</WithBackButton>
										)}
									/>
									<Route path="/hub" exact component={PartnerHubRedirect}/>
									<Route
										path="/terms-of-service"
										exact
										render={() => (
											<WithBackButton>
												<EventNameWrapper>
													<ErrorBoundary
														honeybadger={honeybadger}
														ErrorComponent={ErrorComponent}>
														<TermsOfService/>
													</ErrorBoundary>
												</EventNameWrapper>
											</WithBackButton>
										)}
									/>
									<Route path="/not-found" exact component={NotFound}/>
									<Route component={NotFound}/>
								</Switch>
							</main>
						</>
					) : (
						<Switch>
							<Route exact path="/">
								<Redirect to="/login"/>
							</Route>
								<Route path="/events">
									<Redirect to="/login"/>
								</Route>
								<Route path="/home">
									<Redirect to="/login"/>
								</Route>
								<Route path="/onboarding">
									<Redirect to="/login"/>
								</Route>
								<Route path="/calendar">
									<Redirect to="/login"/>
								</Route>
								<Route path="/participants">
									<Redirect to="/login"/>
								</Route>
								<Route path="/partners">
									<Redirect to="/login"/>
								</Route>
								<Route path="/partner">
									<Redirect to="/login"/>
								</Route>
								<Route path="/recordings">
									<Redirect to="/login"/>
								</Route>
								<Route path="/streams">
									<Redirect to="/login"/>
								</Route>
								<Route path="/user">
									<Redirect to="/login"/>
								</Route>
								<Route
									path="/scan/:scanId"
									render={({ match, location }) => {
										const returnUrl = `/scan/${match.params.scanId}${location.search}`;
										return <Redirect to={`/login?return=${encodeURIComponent(returnUrl)}`}/>;
									}}
								/>
								<Route path="/hub">
									<Redirect to="/login?return=/hub"/>
								</Route>
								<Route
									path="/forgot-password"
									component={() => (
										<WithBackButton to="/login">
											<ForgotPassword/>
										</WithBackButton>
									)}
								/>
								<Route
									exact
									path="/reset-password"
									component={() => <ResetPassword/>}
								/>
								<Route path="/login" exact component={Login}/>
								<Route path="/register" exact component={Register}/>
                                <Route path="/invite" exact component={NewInvite}/>
                                <Route path="/magic-login" exact component={MagicLogin}/>
								<Route
									path="/terms-of-service"
									exact
									render={() => (
										<WithBackButton>
											<EventNameWrapper>
												<ErrorBoundary
													honeybadger={honeybadger}
													ErrorComponent={ErrorComponent}>
													<TermsOfService/>
												</ErrorBoundary>
											</EventNameWrapper>
										</WithBackButton>
									)}
								/>
								<Route
									path="/www.professio.fi"
									component={() => {
										window.location.href = "https://www.professio.fi";
										return null;
									}}
								/>
								<Route path="/not-found" exact component={NotFound}/>
						</Switch>
					)}

					<Navigation hideWhenAdmin user={user}/>
					<Toast/>
					<ApiErrorHandler/>
				</Suspense>
			</ThemeProvider>
		</LocationProvider>
	);
}

const NoMatch = () => {
	const { translations } = useLanguage();
	return <h1 className="center sans">{translations.pageNotFound}</h1>;
};

export default App;
